import styled from 'styled-components'
import { Button } from 'antd'
import theme from '../../theme'

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${theme.midnight50};

  @media (min-width: ${theme.minBreakpoints.tablet}) {
    max-width: 429px;
    margin: 0 auto;
    box-shadow: 2px 2px 10px 2px rgb(197 197 197 / 75%);
  }
`

export const MapArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
`

export const BottomSheet = styled.div`
  flex: 0 0 auto;
  background: #ffffff;
  padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
  z-index: 500;
`

export const Title = styled.h1`
  margin: 0 0 6px;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${theme.midnight500};
`

export const Subtitle = styled.p`
  margin: 0 0 16px;
  font-size: 0.9rem;
  font-weight: 300;
  color: ${theme.slate500};
`

export const ConfirmButton = styled(Button)`
  height: 48px;
  font-size: 1rem;
`

export const StatusMessage = styled.p<{ error?: boolean }>`
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: ${(props) => (props.error ? theme.peach500 : theme.slate500)};
`

// Step 2 form wrapper. The bottom sheet grows to hold the fields; on short
// viewports the fields scroll while the submit button stays anchored below.
// Styled as a plain wrapper (not `styled(Form)`) so the antd Form's generic
// value type survives — `styled()` erases it.
export const AddressFormWrapper = styled.div`
  .ant-form {
    display: flex;
    flex-direction: column;
  }

  .ant-form-item {
    margin-bottom: 12px;
  }
`

export const AddressFields = styled.div`
  max-height: 40vh;
  overflow-y: auto;
`

// Header row for the step-2 form: the title on the left and a subtle "Back"
// control on the right that returns to the map. Mirrors the
// AddressFieldsTitle + CloseDrawerButton affordance in update-address-modal.
export const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`

// Address search on the map step (sc-27850). Sits between the instructions and
// the "Confirm pin" button, so searching reads as the easier first option while
// dragging the pin stays right there as the alternative.
export const SearchBox = styled.div`
  position: relative;
  margin-bottom: 12px;

  /* AutoComplete wraps its input in a span sized to its content by default;
     the search box has to span the sheet like the button below it. */
  .ant-select {
    width: 100%;
  }

  /* Sized to sit comfortably under a thumb, matching the submit button's
     44-48px touch target rather than antd's desktop default. The outer
     .ant-select must be sized too: it does not grow to its selector, so styling
     the selector alone leaves it overflowing onto whatever follows. */
  .ant-select,
  .ant-select-selector {
    height: 44px !important;
  }

  .ant-select-selector {
    border-radius: 8px !important;
    padding-left: 34px !important;
  }

  .ant-select-selection-search,
  .ant-select-selection-placeholder {
    display: flex;
    align-items: center;
  }

  /* The magnifier lives outside the control because rc-select owns the input
     and warns about extra props passed to it (an Input prefix among them);
     absolute-positioning it keeps the affordance without fighting that. */
  .search-icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    z-index: 1;
    color: ${theme.slate500};
    pointer-events: none;
  }
`

// "No match" feedback under the search box. Deliberately styled as a quiet hint
// in the muted body colour, NOT as an error: an empty result set is a normal
// answer and must not read as something the customer has to fix.
export const SearchHint = styled.p`
  margin: 6px 0 0;
  font-size: 0.8rem;
  line-height: 1.3;
  font-weight: 300;
  color: ${theme.slate500};
`

/**
 * One address suggestion in the search dropdown: the place's name over the
 * secondary line that locates it.
 *
 * Both lines are needed, not decorative. Places returns the tower and the
 * district of the same name in one response for "burj khalifa", so on the name
 * alone two rows would be identical and the customer could not tell which one
 * they were tapping.
 *
 * A styled-component rather than CSS nested under `SearchBox`: the dropdown is
 * portalled to the document body, well outside the search box, so a descendant
 * selector there would never reach it. Emitting a class travels with the node.
 */
export const Suggestion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 2px 0;

  /* antd sets nowrap + ellipsis on the option content around this; repeating it
     per line keeps each one clipped on its own instead of the pair overflowing. */
  .suggestion-label,
  .suggestion-secondary {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 1.4rem/1.2rem, not the 0.9rem/0.75rem the rest of this file's copy uses:
     QuReactSDK's base.scss sets html { font-size: 62.5% }, so 1rem is 10px here,
     while antd sizes its own controls in px. At 0.9rem the suggestions rendered
     at 9px against the 14px text the customer had just typed into the box above
     them — legible in a desktop screenshot, too small to read on a phone. These
     match the control instead. */
  .suggestion-label {
    font-size: 1.4rem;
    line-height: 1.3;
    color: ${theme.midnight500};
  }

  .suggestion-secondary {
    font-size: 1.2rem;
    line-height: 1.3;
    font-weight: 300;
    color: ${theme.slate500};
  }
`

export const BackButton = styled(Button)`
  color: ${theme.slate500};
  padding: 0;
  height: auto;
`
